import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";

type Val = "yes" | "no" | string;
type Tier = "anon" | "free" | "pro" | "ent";

interface Feature {
  name: string;
  sub?: string;
  vals: [Val, Val, Val, Val];
}
interface Section {
  title: string;
  features: Feature[];
}

const SECTIONS: Section[] = [
  {
    title: "Probing",
    features: [
      { name: "On-demand GET probe",            vals: ["yes",       "yes",       "yes",        "yes"        ] },
      { name: "POST + custom request headers",  vals: ["no",        "no",        "yes",        "yes"        ] },
      { name: "Global probe regions",           vals: ["8 regions", "8 regions", "8 regions",  "8+ planned" ] },
      { name: "P50 / P95 / P99 breakdown",      vals: ["yes",       "yes",       "yes",        "yes"        ] },
      { name: "DNS · TLS · TTFB timing",        vals: ["yes",       "yes",       "yes",        "yes"        ] },
      { name: "Shareable result link",          vals: ["yes",       "yes",       "yes",        "yes"        ] },
      { name: "Run quota",                      vals: ["3 total",   "20 / day",  "Unlimited",  "Unlimited"  ] },
    ],
  },
  {
    title: "History & Monitoring",
    features: [
      { name: "Run history",        sub: "90-day retention", vals: ["no", "yes",       "yes",      "yes"      ] },
      { name: "Scheduled monitors",                          vals: ["no", "no",        "Up to 20", "Custom"   ] },
      { name: "Minimum poll interval",                       vals: ["no", "no",        "1 minute", "30 seconds"] },
      { name: "Latency trend charts",                        vals: ["no", "no",        "yes",      "yes"      ] },
      { name: "Uptime % tracking",                           vals: ["no", "no",        "yes",      "yes"      ] },
      { name: "Email & webhook alerts",                      vals: ["no", "no",        "yes",      "yes"      ] },
    ],
  },
  {
    title: "Collaboration",
    features: [
      { name: "Team workspaces",          vals: ["no", "no", "no", "yes"] },
      { name: "Shared monitor library",   vals: ["no", "no", "no", "yes"] },
      { name: "Public status page",       vals: ["no", "no", "no", "yes"] },
      { name: "Programmatic API access",  vals: ["no", "no", "no", "yes"] },
      { name: "Embeddable latency badge", vals: ["no", "no", "no", "yes"] },
      { name: "SLA & priority support",   vals: ["no", "no", "no", "yes"] },
    ],
  },
];

function CellVal({ val, tier }: { val: Val; tier: Tier }) {
  if (val === "no") {
    return <span style={{ color: "rgba(152,161,171,0.25)", fontSize: 15, lineHeight: 1 }}>—</span>;
  }
  if (val === "yes") {
    if (tier === "ent") return <span style={{ color: "rgba(152,161,171,0.35)", fontSize: 13 }}>○</span>;
    if (tier === "pro") return <span style={{ color: "#FF2566", fontSize: 14 }}>●</span>;
    return <span style={{ color: "#98A1AB", fontSize: 14 }}>●</span>;
  }
  const color =
    tier === "pro"  ? "#FF2566" :
    tier === "ent"  ? "rgba(152,161,171,0.35)" :
    "#98A1AB";
  return <span style={{ fontSize: 11, color, fontWeight: tier === "pro" ? 600 : 400 }}>{val}</span>;
}

export default function Upgrade() {
  const { isSignedIn } = useAuth();

  return (
    <div style={{ minHeight: "100vh" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "56px 20px 80px" }}>
        <Link to="/" style={backLink}>← Back</Link>

        <div style={{ marginBottom: 52 }}>
          <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: "#FF2566", textTransform: "uppercase" }}>
            Plans & Pricing
          </p>
          <h1 style={{ margin: "0 0 14px", fontSize: "clamp(26px, 4vw, 40px)", fontWeight: 800, color: "#EFF2F5", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
            Upgrade
          </h1>
          <p style={{ margin: 0, fontSize: 16, color: "#98A1AB", maxWidth: 500, lineHeight: 1.65 }}>
            Every probe is real — no synthetic data, no cloud-to-cloud shortcuts.
            Your endpoint, measured from actual global edge locations.
          </p>
        </div>

        <div className="glass" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 580 }}>
            <colgroup>
              <col style={{ width: "35%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "15%" }} />
            </colgroup>

            <thead>
              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <th style={{ padding: "28px 20px 22px", textAlign: "left", verticalAlign: "bottom" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(152,161,171,0.4)" }}>
                    Feature
                  </span>
                </th>

                <th style={{ padding: "28px 10px 22px", textAlign: "center", verticalAlign: "bottom" }}>
                  <TierHeader name="Anonymous" price="Free" note="No account" cta={null} />
                </th>

                <th style={{ padding: "28px 10px 22px", textAlign: "center", verticalAlign: "bottom" }}>
                  <TierHeader
                    name="Free" price="Free" note="After sign-up"
                    cta={isSignedIn
                      ? <span className="btn-glass" style={{ ...ctaStyle, opacity: 0.4, cursor: "default" }}>Current</span>
                      : <Link to="/sign-up" className="btn-glass" style={ctaStyle}>Sign up →</Link>
                    }
                  />
                </th>

                <th style={{ padding: "28px 12px 22px", textAlign: "center", verticalAlign: "bottom", background: "rgba(255,37,102,0.06)", borderTop: "2px solid #FF2566" }}>
                  <TierHeader
                    name="Pro" nameColor="#FF2566"
                    price="$12" priceSuffix="/mo"
                    note="$99 / year · save 31%"
                    cta={<Link to="/checkout" className="btn-primary" style={ctaStyle}>Start Pro →</Link>}
                  />
                </th>

                <th style={{ padding: "28px 10px 22px", textAlign: "center", verticalAlign: "bottom" }}>
                  <TierHeader
                    name="Enterprise" nameColor="rgba(152,161,171,0.4)"
                    price="Custom" priceColor="rgba(152,161,171,0.35)"
                    note="Teams & SLA" noteColor="rgba(152,161,171,0.3)"
                    cta={
                      <span style={{ display: "inline-block", padding: "5px 10px", fontSize: 10, fontWeight: 600, background: "rgba(18,18,18,0.5)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 3, color: "#98A1AB", opacity: 0.5 }}>
                        Coming soon
                      </span>
                    }
                  />
                </th>
              </tr>
            </thead>

            <tbody>
              {SECTIONS.flatMap((section, si) => [
                <tr key={`sec-${si}`}>
                  <td colSpan={5} style={{ padding: si === 0 ? "18px 20px 7px" : "26px 20px 7px", fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(152,161,171,0.4)", borderTop: si === 0 ? "none" : "1px solid rgba(255,255,255,0.06)" }}>
                    {section.title}
                  </td>
                </tr>,
                ...section.features.map((feature, fi) => (
                  <tr key={`feat-${si}-${fi}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    <td style={{ padding: "10px 20px" }}>
                      <span style={{ fontSize: 13, color: "#EFF2F5" }}>{feature.name}</span>
                      {feature.sub && <span style={{ display: "block", fontSize: 11, color: "#98A1AB", marginTop: 1 }}>{feature.sub}</span>}
                    </td>
                    <td style={{ padding: "10px 10px", textAlign: "center" }}><CellVal val={feature.vals[0]} tier="anon" /></td>
                    <td style={{ padding: "10px 10px", textAlign: "center" }}><CellVal val={feature.vals[1]} tier="free" /></td>
                    <td style={{ padding: "10px 12px", textAlign: "center", background: "rgba(255,37,102,0.04)" }}><CellVal val={feature.vals[2]} tier="pro" /></td>
                    <td style={{ padding: "10px 10px", textAlign: "center" }}><CellVal val={feature.vals[3]} tier="ent" /></td>
                  </tr>
                )),
              ])}

              <tr style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <td style={{ padding: "18px 20px" }} />
                <td style={{ padding: "18px 10px" }} />
                <td style={{ padding: "18px 10px", textAlign: "center" }}>
                  {isSignedIn
                    ? <span className="btn-glass" style={{ ...ctaStyle, padding: "8px 18px", opacity: 0.4, cursor: "default" }}>Current</span>
                    : <Link to="/sign-up" className="btn-glass" style={{ ...ctaStyle, padding: "8px 18px" }}>Sign up →</Link>
                  }
                </td>
                <td style={{ padding: "18px 12px", textAlign: "center", background: "rgba(255,37,102,0.04)" }}>
                  <Link to="/checkout" className="btn-primary" style={{ ...ctaStyle, padding: "8px 18px" }}>Start Pro →</Link>
                </td>
                <td style={{ padding: "18px 10px" }} />
              </tr>
            </tbody>
          </table>
        </div>

        <p style={{ marginTop: 20, fontSize: 12, color: "#98A1AB", textAlign: "center", lineHeight: 1.6 }}>
          Enterprise — team workspaces, status pages, programmatic API access, and SLA — is planned for a future release.{" "}
          <a href="mailto:hello@worldrtt.com" style={{ color: "rgba(255,37,102,0.6)" }}>Reach out early</a>{" "}
          if your team needs it sooner.
        </p>
      </div>
    </div>
  );
}

interface TierHeaderProps {
  name: string; nameColor?: string;
  price: string; priceSuffix?: string; priceColor?: string;
  note: string; noteColor?: string;
  cta: React.ReactNode;
}

function TierHeader({ name, nameColor, price, priceSuffix, priceColor, note, noteColor, cta }: TierHeaderProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: nameColor ?? "#98A1AB" }}>{name}</span>
      <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
        <span style={{ fontSize: 22, fontWeight: 800, color: priceColor ?? "#EFF2F5", letterSpacing: "-0.02em" }}>{price}</span>
        {priceSuffix && <span style={{ fontSize: 11, color: "#98A1AB" }}>{priceSuffix}</span>}
      </div>
      <span style={{ fontSize: 10, color: noteColor ?? "#98A1AB", marginBottom: 8 }}>{note}</span>
      {cta}
    </div>
  );
}

const backLink: React.CSSProperties = { display: "inline-block", marginBottom: 20, fontSize: 13, color: "#98A1AB", textDecoration: "none" };
const ctaStyle: React.CSSProperties = { display: "inline-block", padding: "6px 14px", fontSize: 11, fontWeight: 600, textAlign: "center" };
